import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bell, FileSearch, RotateCcw, Search, SlidersHorizontal, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SearchPagination from '../components/SearchPagination';
import SearchCountLabel from '../components/SearchCountLabel';
import { SearchCount } from '../api/searchCount';
import { useSearchCount } from '../hooks/useSearchCount';
import { isSearchCursorError, releaseSearchSession, useSearchCursor } from '../hooks/useSearchCursor';
import SearchProgress from '../components/SearchProgress';
import LotFilterFields from '../components/LotFilterFields';
import LotResultsTable from '../components/LotResultsTable';
import EnstruCategoryPicker, { PickedCategory, PickedCode } from '../components/EnstruCategoryPicker';
import EnstruCategoryButton from '../components/EnstruCategoryButton';
import {
  LotListItem, LotRefsResponse, getLotRefs, searchLots,
} from '../api/lots';
import { categoryGroup, categorySelectionParams, readCategorySelection } from '../utils/categorySelection';
import ProcurementAccessGate from '../components/ProcurementAccessGate';
import { useProcurementAccess } from '../context/ProcurementAccessContext';
import SearchAlertsPanel from '../components/SearchAlertsPanel';
import SearchAlertDialog from '../components/SearchAlertDialog';
import { getSearchAlert, SearchAlert, SearchAlertFilters } from '../api/searchAlerts';
import TrackProcurementDialog from '../components/TrackProcurementDialog';
import { analytics } from '../analytics';
import ProcurementPreview from '../components/ProcurementPreview';
import ProcurementTrialPanel from '../components/ProcurementTrialPanel';
import { useAuth } from '../context/AuthContext';
import { readProcurementSubject } from '../utils/procurementSubject';

const PAGE_SIZE = 20;

export default function LotsPage() {
  return <LotsWorkspace />;
}

function LotsWorkspace() {
  const { t } = useLanguage();
  const { unreadCount } = useProcurementAccess();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const alerts = params.get('tab') === 'alerts';
  const workspaceId = useId();
  const activeKey = alerts ? 'alerts' : 'lots';
  const navigateTabs = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
    const current = tabs.indexOf(document.activeElement as HTMLButtonElement);
    if (current < 0) return;
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1
      : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    event.preventDefault(); tabs[next].focus(); tabs[next].click();
  };
  return <div className="w-full min-w-0">
    <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-3 px-4 pt-6 pb-6 sm:px-6 sm:pt-8">
    <div role="tablist" aria-label={t('procurement.lots')} onKeyDown={navigateTabs}
      className="inline-flex max-w-full items-stretch gap-1 rounded-xl border border-slate-200 bg-white p-1">
      {(['lots', 'alerts'] as const).map((key) => <button key={key} type="button" role="tab"
        id={`${workspaceId}-${key}-tab`} aria-controls={`${workspaceId}-${key}-panel`}
        aria-selected={activeKey === key} tabIndex={activeKey === key ? 0 : -1}
        onClick={() => setParams((previous) => { const next = new URLSearchParams(previous); if (key === 'alerts') next.set('tab', key); else { next.delete('tab'); next.delete('alert'); } return next; })}
        className={`procurement-focus inline-flex min-h-[44px] min-w-0 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold leading-5 transition-colors sm:px-5 ${activeKey === key ? 'bg-goszakup-700 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
        {key === 'lots' ? <FileSearch aria-hidden="true" className="hidden h-4 w-4 shrink-0 sm:block" /> : <Bell aria-hidden="true" className="hidden h-4 w-4 shrink-0 sm:block" />}
        <span>{t(`procurement.${key}`)}</span>
        {key === 'alerts' && unreadCount != null && unreadCount > 0 && <span className={`rounded-full px-2 py-0.5 text-xs tabular-nums ${activeKey === key ? 'bg-white/20 text-white' : 'bg-sky-100 text-sky-800'}`}>{unreadCount}</span>}
      </button>)}
    </div>
    <Link to="/tracker" title={t('tracking.linkHint')} className="procurement-button text-goszakup-700">
      <Bell aria-hidden="true" className="h-4 w-4 shrink-0" />{t('nav.tracker')}
    </Link>
    </div>
    <div role="tabpanel" id={`${workspaceId}-lots-panel`} aria-labelledby={`${workspaceId}-lots-tab`} hidden={alerts}>
      {!alerts && <ProcurementAccessGate feature="lots" fallback={<ProcurementPreview />}>
        <LotsContent />
      </ProcurementAccessGate>}
    </div>
    <div role="tabpanel" id={`${workspaceId}-alerts-panel`} aria-labelledby={`${workspaceId}-alerts-tab`} hidden={!alerts}>
      {alerts && (user ? <SearchAlertsPanel key={`${user.email}:${user.role}`} /> : <div className="mx-auto max-w-7xl px-4 pb-6 sm:px-6"><ProcurementTrialPanel /></div>)}
    </div>
  </div>;
}

function LotsContent() {
  const { t } = useLanguage();
  const { allowed, access } = useProcurementAccess();
  const searchFormId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const { cursorForPage, rememberPage } = useSearchCursor(searchParams);

  const [items, setItems] = useState<LotListItem[]>([]);
  const [trackingLot, setTrackingLot] = useState<LotListItem | null>(null);
  const [trackedAnnos, setTrackedAnnos] = useState<string[]>([]);
  const [countSource, setCountSource] = useState<SearchCount | null>(null);
  const count = useSearchCount(countSource);
  const total = count.total;
  const [hasMore, setHasMore] = useState(false);
  const [cursorMode, setCursorMode] = useState(false);
  const [cursorExpired, setCursorExpired] = useState(false);
  const [sortDegraded, setSortDegraded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchFailed, setSearchFailed] = useState(false);
  const [refs, setRefs] = useState<LotRefsResponse | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const filterPanel = useRef<HTMLDivElement>(null);
  const [savedFilters, setSavedFilters] = useState<SearchAlertFilters | null>(null);
  const [editingAlert, setEditingAlert] = useState<SearchAlert | undefined>();
  const [editFailed, setEditFailed] = useState(false);
  const editId = searchParams.get('edit_alert') || '';
  const canSaveSearch = allowed('search_alerts');
  useEffect(() => {
    setEditingAlert(undefined); setEditFailed(false);
    if (!editId || !canSaveSearch) return;
    const controller = new AbortController();
    getSearchAlert(editId, controller.signal).then((item) => { if (!controller.signal.aborted) setEditingAlert(item); })
      .catch(() => { if (!controller.signal.aborted) setEditFailed(true); });
    return () => controller.abort();
  }, [editId, canSaveSearch]);

  // Фильтры (истина — в URL, чтобы ссылками можно было делиться)
  const q = searchParams.get('q') || '';
  const page = Math.max(0, parseInt(searchParams.get('page') || '0', 10) || 0);
  const cursor = cursorForPage(page);
  const lotNumber = searchParams.get('lot_number') || '';
  const annoNumber = searchParams.get('anno') || '';
  const customerBin = searchParams.get('customer_bin') || '';
  const customerName = searchParams.get('customer') || '';
  const statusIds = useMemo(() => (searchParams.get('status') || '').split(',').filter(Boolean).map(Number), [searchParams]);
  const methodIds = useMemo(() => (searchParams.get('method') || '').split(',').filter(Boolean).map(Number), [searchParams]);
  const subjectType = readProcurementSubject(searchParams.get('subject_type'));
  const enstruCode = searchParams.get('enstru') || '';
  const kpvedSection = searchParams.get('section') || '';
  const catGroup = searchParams.get('cat_group') || '';
  const categoryLabel = searchParams.get('cat_label') || '';
  const pickedCodes = useMemo<PickedCode[]>(
    () => readCategorySelection(enstruCode, categoryLabel), [enstruCode, categoryLabel],
  );
  const enstruCodes = useMemo(() => pickedCodes.map((item) => item.code), [pickedCodes]);
  const amountFrom = searchParams.get('amount_from') || '';
  const amountTo = searchParams.get('amount_to') || '';
  const region = searchParams.get('region') || '';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';
  const sort = searchParams.get('sort') || 'newest';

  const [qInput, setQInput] = useState(q);
  useEffect(() => setQInput(q), [q]);

  const patchParams = useCallback((patch: Record<string, string | null>, resetPage = true) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: false });
  }, [setSearchParams]);

  useEffect(() => { getLotRefs().then(setRefs).catch(() => {}); }, []);

  const load = useCallback(async () => {
    // отменяем предыдущий запрос — гонок и «поломок» при долгом поиске не будет
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setCountSource(null);
    setSearchFailed(false);
    setCursorExpired(false);
    try {
      const data = await searchLots({
        q: q || undefined,
        lot_number: lotNumber || undefined,
        anno_number: annoNumber || undefined,
        customer_bin: customerBin || undefined,
        customer_name: customerName || undefined,
        status: statusIds.length ? statusIds : undefined,
        trade_method: methodIds.length ? methodIds : undefined,
        subject_type: subjectType,
        enstru_code: enstruCodes.length ? enstruCodes : undefined,
        kpved_section: !enstruCodes.length && kpvedSection ? kpvedSection : undefined,
        group: !enstruCodes.length && kpvedSection ? categoryGroup(catGroup) : undefined,
        amount_from: amountFrom ? Number(amountFrom) : undefined,
        amount_to: amountTo ? Number(amountTo) : undefined,
        region: region || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        sort: (sort as any) || 'newest',
        skip: page * PAGE_SIZE,
        cursor,
        limit: PAGE_SIZE,
      }, controller.signal);
      if (controller.signal.aborted) {
        if (page === 0) releaseSearchSession(data.search_session);
        return;
      }
      setItems(data.items);
      if (page === 0) analytics.search('lots', data.items.length);
      rememberPage(page, data.next_cursor, data.search_session);
      setCursorMode(data.pagination_mode === 'cursor');
      setCountSource(data);
      setHasMore(data.has_more ?? data.items.length === PAGE_SIZE);
      setSortDegraded(!!data.sort_degraded);
      setLoading(false);
    } catch (error) {
      if (controller.signal.aborted) return; // заменён более новым запросом
      setItems([]);
      setCursorExpired(isSearchCursorError(error));
      setSearchFailed(true);
      setLoading(false);
    }
  }, [q, lotNumber, annoNumber, customerBin, customerName, statusIds, methodIds, subjectType,
      enstruCodes, kpvedSection, catGroup, amountFrom, amountTo, region, dateFrom, dateTo, sort, page,
      cursor, rememberPage]);

  useEffect(() => { load(); return () => abortRef.current?.abort(); }, [load]);

  const onPickCategory = (picked: PickedCategory) => {
    setPickerOpen(false);
    // «весь раздел» заменяет выбранные коды
    patchParams({
      enstru: picked.code || null,
      section: picked.section || null,
      cat_group: picked.group || null,
      cat_label: picked.label,
    });
  };

  const onApplyCategories = (codes: PickedCode[]) => {
    setPickerOpen(false);
    patchParams(categorySelectionParams(codes));
  };

  const removeCategory = (code: string) => {
    const rest = pickedCodes.filter((c) => c.code !== code);
    onApplyCategories(rest);
  };

  const clearCategory = () => patchParams({ enstru: null, section: null, cat_group: null, cat_label: null });

  const categoryActive = enstruCodes.length > 0 || !!kpvedSection;
  const categoryBtnText = enstruCodes.length > 1
    ? `${t('lots.categoryBtn')}: ${enstruCodes.length}`
    : (pickedCodes[0]?.label || (kpvedSection ? categoryLabel || kpvedSection : '') || t('lots.categoryBtn'));

  const hasFilters =
    !!(q || lotNumber || annoNumber || customerBin || customerName || amountFrom || amountTo
       || region || enstruCodes.length || kpvedSection || dateFrom || dateTo || subjectType) || statusIds.length > 0 || methodIds.length > 0;

  const resetAll = () => {
    setQInput('');
    setSearchParams(new URLSearchParams(), { replace: false });
  };

  const MAX_PAGES = Math.floor(1000000 / PAGE_SIZE) + 1;

  const subscribe = () => {
    // Read uncontrolled fields too: clicking Subscribe must not discard text
    // still focused or typed before pressing the Search button.
    const draft = (key: string, fallback: string) => filterPanel.current?.querySelector<HTMLInputElement>(`[data-alert-filter="${key}"]`)?.value.trim() ?? fallback;
    const from = draft('amount_from', amountFrom), to = draft('amount_to', amountTo);
    setSavedFilters({ q: qInput.trim() || undefined, lot_number: draft('lot_number', lotNumber) || undefined,
      anno_number: draft('anno_number', annoNumber) || undefined, customer_bin: draft('customer_bin', customerBin) || undefined,
      customer_name: draft('customer_name', customerName) || undefined, status: statusIds.length ? statusIds : undefined,
      trade_method: methodIds.length ? methodIds : undefined, enstru_code: enstruCodes.length ? enstruCodes : undefined,
      subject_type: subjectType,
      kpved_section: !enstruCodes.length && kpvedSection ? kpvedSection : undefined,
      group: !enstruCodes.length && kpvedSection ? categoryGroup(catGroup) : undefined,
      amount_from: from ? Number(from) : undefined, amount_to: to ? Number(to) : undefined,
      region: region || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined,
      sort: sort as SearchAlertFilters['sort'] });
  };

  return (
    <div className="w-full min-w-0 bg-slate-50 pb-6">
      {/* Hero */}
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div>
            <div>
              <h1 className="workspace-title">{t('lots.title')}</h1>
              <p className="workspace-subtitle mt-1">{t('lots.subtitle')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Панель поиска: все критерии видимы, как на портале госзакупок */}
        <div ref={filterPanel} className="workspace-card mb-6 grid grid-cols-1 gap-x-3 p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:p-6">
          {/* Строка полнотекстового поиска */}
          <form
            id={searchFormId}
            className="mb-4 flex min-w-0 flex-col gap-3 sm:col-start-1 sm:row-start-1 sm:flex-row"
            onSubmit={(e) => { e.preventDefault(); patchParams({ q: qInput.trim() || null }); }}
          >
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder={t('lots.searchPlaceholder')}
                aria-label={t('lots.searchPlaceholder')}
                className="procurement-input pl-10 pr-4"
              />
            </div>
            <EnstruCategoryButton
              active={categoryActive}
              open={pickerOpen}
              label={categoryBtnText}
              onClick={() => setPickerOpen(true)}
            />
          </form>

          {/* Чипы выбранных категорий */}
          {categoryActive && (
            <div className="mb-4 flex flex-wrap gap-2 sm:col-span-2">
              {pickedCodes.map((c) => (
                <span
                  key={c.code}
                  className="inline-flex items-center gap-1.5 max-w-[320px] pl-3 pr-2 py-1.5 rounded-full bg-goszakup-50 border border-goszakup-200 text-xs font-semibold text-goszakup-700"
                  title={c.label}
                >
                  <span className="truncate">{c.label}</span>
                  <button
                    type="button"
                    aria-label={`${t('lots.picker.remove')}: ${c.label}`}
                    onClick={() => removeCategory(c.code)}
                    className="p-0.5 rounded-full hover:bg-goszakup-100 text-goszakup-500"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
              {!enstruCodes.length && <span className="text-xs text-goszakup-700 py-1.5">{categoryBtnText}</span>}
              <button type="button" onClick={clearCategory} className="px-2 py-1.5 text-xs font-semibold text-gray-500 hover:text-goszakup-700">
                {t('lots.picker.clear')}
              </button>
            </div>
          )}

          {/* Критерии — постоянная сетка */}
          <div className="min-w-0 sm:col-span-2">
            <LotFilterFields params={searchParams} refs={refs} commitOnBlur
              onChange={(key, value) => patchParams({ [key]: value || null })} />
          </div>
          <button
            type="submit"
            form={searchFormId}
            className="procurement-button-primary mt-5 w-full shrink-0 px-6 sm:col-start-2 sm:row-start-1 sm:mt-0 sm:w-auto sm:self-start"
          >
            <Search aria-hidden="true" className="w-4 h-4" /> {t('lots.searchBtn')}
          </button>
          <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
            {canSaveSearch && <button type="button" onClick={subscribe} disabled={!!editId && !editingAlert}
              className="procurement-focus inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-semibold leading-5 text-goszakup-700 transition-colors hover:border-goszakup-300 hover:bg-goszakup-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto">
              <Bell aria-hidden="true" className="h-4 w-4 shrink-0" />
              {t(editId ? 'procurement.editSearch' : 'procurement.saveSearch')}
            </button>}
            <button type="button" onClick={resetAll} disabled={!hasFilters} className="procurement-button w-full sm:ml-auto sm:w-auto">
              <RotateCcw aria-hidden="true" className="h-4 w-4 shrink-0" />{t('lots.filters.reset')}
            </button>
          </div>
          {canSaveSearch && editFailed && <p role="alert" className="mt-2 text-red-700 sm:col-span-2">{t('procurement.error')}</p>}
        </div>

        {savedFilters && canSaveSearch && <SearchAlertDialog filters={savedFilters} existing={editingAlert}
          onClose={() => setSavedFilters(null)} onSaved={() => { setSavedFilters(null); patchParams({ tab: 'alerts', edit_alert: null }); }} />}
        {/* Итоги поиска */}
        {!loading && !searchFailed && (
          <SearchCountLabel label={t('lots.found')} {...count}
            retry={count.canRetry ? count.retry : load} canRetry={count.canRetry || count.status === 'error'} />
        )}
        {!loading && !searchFailed && sortDegraded && (
          <p role="status" className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">{t('lots.sortDegraded')}</p>
        )}

        {/* Результаты */}
        {loading ? (
          <SearchProgress active label={t('lots.searching')} />
        ) : searchFailed ? (
          <div className="text-center py-24 text-gray-400">
            <SlidersHorizontal className="w-12 h-12 mx-auto opacity-30 mb-3" />
            <p className="text-sm">{t(cursorExpired ? 'lots.cursorExpired' : 'lots.searchFailed')}</p>
            <button type="button" onClick={() => cursorExpired ? patchParams({ page: null }) : load()}
              className="mt-3 text-sm font-semibold text-goszakup-700 underline">{t(cursorExpired ? 'lots.restartSearch' : 'lots.retry')}</button>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24 text-gray-400">
            <FileSearch className="w-12 h-12 mx-auto opacity-30 mb-3" />
            <p className="text-sm">{t('lots.empty')}</p>
          </div>
        ) : (
          <LotResultsTable items={items} refs={refs} onTrack={setTrackingLot} trackedAnnos={trackedAnnos} />
        )}

        {/* Пагинация */}
        {!loading && (!searchFailed || page > 0) && (
          <SearchPagination page={page} pageSize={PAGE_SIZE} total={total}
            maxPages={cursorMode ? Number.POSITIVE_INFINITY : MAX_PAGES} cursorMode={cursorMode}
            hasMore={!searchFailed && hasMore} onPageChange={(next) => {
            patchParams({ page: String(next) }, false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }} />
        )}
        {access?.trial?.status === 'active' && <div className="mt-6"><ProcurementTrialPanel compact /></div>}
      </div>

      <EnstruCategoryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={onPickCategory}
        selected={pickedCodes}
        onApply={onApplyCategories}
      />
      <TrackProcurementDialog open={trackingLot !== null} onClose={() => setTrackingLot(null)}
        lotId={trackingLot?.id} numberAnno={trackingLot?.trdBuyNumberAnno || undefined}
        onTracked={(entry) => setTrackedAnnos((previous) => previous.includes(entry.number_anno) ? previous : [...previous, entry.number_anno])} />
    </div>
  );
}
